import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import TwoLineCardSkeleton from "../components/SkeletonCards/TwoLineCardSkeleton";
import useFetch from "../hooks/useFetch";
import { Event } from "../types";
import EntityAddUpdateForm from "../components/EntityAddUpdateForm/EntityAddUpdateForm";
import TextInput from "../components/ui/Input/TextInput";
import SelectInput from "../components/ui/Input/SelectInput";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from '../components/ui/Input/Input.module.css';
import { format } from 'date-fns';

import '../styles/EventToggle.css';
import { toast } from "react-toastify";

const Events: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const params = useParams<{ competition_id: string }>();
  const competition_id = params?.competition_id;
  const [eventId, setEventId] = useState<number>(NaN);
  const [eventDate, setEventDate] = useState<string | Date>("");
  const [location, setLocation] = useState<string>("");
  const [competition, setCompetition] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUpdateActive, setIsUpdateActive] = useState(false);

  const { data, setData, errorContent, isLoading, fetchData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/events`,
    method: "POST",
  });

  const { fetchData: fetchCompetitionData, data: competitionsData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/competitions/`,
    method: "GET",
  });

  useEffect(() => {
    fetchData({ competitions_id: competition_id });
    fetchCompetitionData();
  }, []);

  if (errorContent) return errorContent;

  const clearInputFields = () => {
    setLocation("");
    setEventDate("");
    setCompetition("");
  };

  const handleToggleStatus = async (event: Event) => {
    const action = event.status === 1 ? "disable" : "enable";
    const confirmed = window.confirm(`Are you sure you want to ${action} this event?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/events/toggle-status/${event.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setData((prevData: any) => ({
          ...prevData,
          events: prevData.events.map((e: Event) =>
            e.id === event.id ? { ...e, status: result.data.status } : e
          ),
        }));
        toast.info(`Event ${action}d successfully!`);
      } else {
        setErrorMessage("Failed to update event status.");
      }
    } catch (err) {
      setErrorMessage("Failed to update event status, please try again.");
    }
  };
  
  const handleDeleteEvent = async (eventId: number) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Event?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/events/delete/${eventId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSuccessMessage("Event deleted successfully!");
        setErrorMessage(null);
        setData((prevData: any) => ({
          ...prevData,
          events: prevData.events.filter(
            (event: Event) => event.id !== eventId
          ),
        }));
      } else {
        let errorMessage = "Error deleting Event: ";
        let errorMessagedetail;
        if (response.headers.get("content-type")?.includes("application/json")) {
          const result = await response.json();
          if (result.data?.error_message?.includes("Cannot delete or update a parent row")) {
              errorMessagedetail = "Cannot delete event with performances, divisions attached. Please remove performances and divisions or deactivate it instead.";
          } else {
            errorMessagedetail = result.message || errorMessage;
          }
          errorMessage += errorMessagedetail;
        } else {
          console.warn("Response is not JSON:", response);
        }
        setErrorMessage(errorMessage);
        setSuccessMessage(null);
      }
    } catch (err) {
      console.error("Delete Event Error:", err);
      setErrorMessage("Failed to delete event, please try again.");
      setSuccessMessage(null);
    }
  };

  const populateFieldsForUpdate = (event: {
    id: number;
    location: string;
    event_date: string;
    competition: string;
  }) => {
    const formatedEventDate = new Date(event.event_date);
    const formatedEventDateString = formatedEventDate.toISOString().split("T")[0];
    setLocation(event.location);
    setEventDate(formatedEventDateString);
    setCompetition(event.competition);
    setIsUpdateActive(true);
  };

  const handleSubmitEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const fields = {
      location: { value: location, message: "Location is empty." },
      eventDate: { value: eventDate, message: "Event date is empty." },
      competition: { value: competition, message: "Competition must be selected." }
    };
    for (const { value, message } of Object.values(fields)) {
      if (typeof value === 'string' && !value.trim()) { setErrorMessage(message); return; }
      if (value instanceof Date && isNaN(value.getTime())) { setErrorMessage(message); return; }
    }
    const formattedEventDate = format(new Date(eventDate), 'yyyy-MM-dd');
    const eventData = { competitions_id: competition, event_date: formattedEventDate, location };
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      const response = await fetch(`${import.meta.env.VITE_API_URL}/events/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(eventData),
      });
      const result = await response.json();
      if (response.ok) {
        setSuccessMessage("Event added successfully!");
        setErrorMessage(null);
        setData((prevData: any) => ({ ...prevData, events: [...prevData.events, result.data] }));
      } else {
        setErrorMessage(result.errors[0].error || "Error adding event");
        setSuccessMessage(null);
      }
      clearInputFields();
    } catch (err) {
      setErrorMessage("Failed to add event, please try again.");
      setSuccessMessage(null);
    }
  };

  const sendUpdatedEventData = async (eventId: number) => {
    const fields = {
      location: { value: location, message: "Location is empty." },
      eventDate: { value: eventDate, message: "Event date is empty." },
      competition: { value: competition, message: "Competition must be selected." }
    };
    for (const { value, message } of Object.values(fields)) {
      if (typeof value === 'string' && !value.trim()) { setErrorMessage(message); return; }
      if (value instanceof Date && isNaN(value.getTime())) { setErrorMessage(message); return; }
    }
    const formattedEventDate = format(new Date(eventDate), 'yyyy-MM-dd');
    const eventData = { competitions_id: competition, event_date: formattedEventDate, location };
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      const response = await fetch(`${import.meta.env.VITE_API_URL}/events/update/${eventId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(eventData),
      });
      const result = await response.json();
      if (response.ok) {
        setSuccessMessage("Event updated successfully!");
        setErrorMessage(null);
        setData((prevData: any) => ({
          ...prevData,
          events: prevData.events.map((event: Event) =>
            event.id === result.data.id ? { ...event, ...result.data } : event
          ),
        }));
      } else {
        setErrorMessage(result.errors[0].error || "Error updating event");
        setSuccessMessage(null);
      }
    } catch (err) {
      setErrorMessage("Failed to update event, please try again.");
      setSuccessMessage(null);
    }
  };

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendUpdatedEventData(eventId);
    clearInputFields();
    setIsUpdateActive(false);
  };

  const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;
  const canSeeAdminPerformances = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "adminperformances")?.ModulesPermissions?.view;
  const canSeeDivisions = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "divisions")?.ModulesPermissions?.view;
  const canSeeMusicBtn = !!JSON.parse(localStorage.getItem("user") || '{}')?.modules?.find((m:any) => m.title.toLowerCase() === "eventsongs")?.ModulesPermissions?.view;
  const isAdmin = Number(currentRole) === 1;
  const isTalktape = Number(currentRole) === 9;
  const isBlockedFromBoxClick = (![1,8,7,3,4].includes(currentRole) && canSeeDivisions);

  return (
    <section>
      
      {isAdmin ? (
        <div className="form-container">
          <h2 style={{ fontSize: "24px" }}>Add/Edit Event</h2>
          <EntityAddUpdateForm
            handleUserSubmit={isUpdateActive ? handleUpdateEvent : handleSubmitEvent}
          >
            <TextInput
              placeholder="Location"
              value={location}
              onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setLocation(e.target.value); }}
            />
            <DatePicker
              id="eventDate"
              selected={eventDate ? new Date(eventDate) : null}
              onChange={(date: Date | null) => { if (date) setEventDate(date); }}
              dateFormat="MM/dd/yyyy"
              autoComplete="off"
              placeholderText="Event Date"
              isClearable={false}
              customInput={<input className={styles.singleInput} style={{ width: "auto" }} />}
            />
            <SelectInput
              value={competition || ""}
              options={competitionsData.map((comp: any) => ({
                value: comp.id,
                label: (comp.title + " - " + comp.start_date),
              }))}
              name="competitions"
              onChange={(e) => { setErrorMessage(null); setSuccessMessage(null); setCompetition(e.target.value); }}
            />
          </EntityAddUpdateForm>
          {successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
          {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
        </div>
      ) : null}

      <div className="container">
        <div className="div-header-title-section">
          <h1 className="judgingapp-heading2">{data?.competition?.title}</h1>
          <div
            className="link-block w-inline-block cursor-pointer"
            onClick={() => navigate("/competitions")}
          >
            <img src="/images/back-icon.png" loading="lazy" alt="" className="image-2" />
            <h1 className="back-text-link">Home</h1>
          </div>
          <p className="paragraph-2">
            {data?.competition?.start_date} - {data?.competition?.end_date}
          </p>
        </div>

        <div className="events__mainBox">
          {isLoading ? (
            <TwoLineCardSkeleton cardsCount={1} baseColor="#d7daef" highlightColor="#e4e4e4" />
          ) : (
            data?.events?.map((event: Event) => {
              const isActive = event.status === 1;

              return (
                <div
                  key={event.id}
                  className={`small-card cursor-pointer scale--md${!isActive ? " small-card--inactive" : ""}`}
                  onClick={() => {
                    if (!isActive) return; // block click when inactive
                    if (isTalktape) navigate(`/admin/performances/${event.id}`);
                    else if (isBlockedFromBoxClick) navigate(`/divisions/${event.id}`);
                  }}
                >
                  <h3 className="small-card__primaryHeading">{event.event_date}</h3>
                  <p className="small-card__secondaryHeading">{event.location}</p>

                  {!isBlockedFromBoxClick && !isTalktape &&
                    ((canSeeAdminPerformances && !isTalktape) || canSeeDivisions || canSeeMusicBtn) && (
                    <div className="small-card-btnsBox" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                      {canSeeAdminPerformances && (
                        <button
                          className="action-btn"
                          disabled={!isActive}
                          onClick={() => navigate(`/admin/performances/${event.id}`)}
                        >
                          Performances
                        </button>
                      )}
                      {canSeeDivisions && (
                        <button
                          className="action-btn"
                          disabled={!isActive}
                          onClick={() => navigate(`/divisions/${event.id}`)}
                        >
                          Divisions
                        </button>
                      )}
                      {canSeeMusicBtn && (
                        <button
                          className="action-btn"
                          disabled={!isActive}
                          onClick={() => navigate(`/music/${event.id}`)}
                        >
                          Music
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button
                            className="action-btn"
                            onClick={() => {
                              setEventId(event.id);
                              populateFieldsForUpdate({
                                id: event.id,
                                event_date: event.event_date,
                                location: event.location,
                                competition: String(data?.competition.id),
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Delete
                          </button>

                          {isAdmin && (
                            <div
                              className="event-toggle-wrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <label className="event-toggle" title={isActive ? "Disable event" : "Enable event"}>
                                <input
                                  type="checkbox"
                                  checked={isActive}
                                  onChange={() => handleToggleStatus(event)}
                                />
                                <span className="event-toggle__slider" />
                              </label>
                            </div>
                          )}


                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
});

export default Events;